<!-- Dans ItemList.vue, modifiez le template -->
<template>
  <div class="item-container">
    <PageHeader
      title="Item Collection"
      subtitle="Manage and organize your question items"
      icon="bi-collection"
      color="purple"
    >
      <template #actions>
        <input id="file-input" type="file" accept=".xml" class="d-none" @change="handleQtiImport" />
        <button type="button" class="btn-import-qti" @click="triggerFileSelect">
          <i class="bi bi-file-earmark-arrow-up"></i>
          <span>Import QTI Item</span>
        </button>
      </template>
    </PageHeader>

  <!-- Statistics Indicators -->
  <div class="statistics-container">
    <div class="stat-card stat-card-blue">
      <div class="stat-icon">
        <i class="bi bi-collection"></i>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ statistics.totalItems }}</div>
        <div class="stat-label">Total Items</div>
      </div>
    </div>

    <div class="stat-card stat-card-purple">
      <div class="stat-icon">
        <i class="bi bi-bookmark"></i>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ statistics.totalSubjects }}</div>
        <div class="stat-label">Subjects</div>
      </div>
    </div>

    <!-- Type distribution - one card per type -->
    <div
      v-for="(count, type, index) in limitedTypeDistribution"
      :key="type"
      :class="['stat-card', getTypeCardClass(index)]"
    >
      <div class="stat-icon">
        <i :class="getTypeIcon(type)"></i>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ count }}</div>
        <div class="stat-label">{{ formatTypeName(type) }}</div>
      </div>
    </div>
  </div>

    <div class="ItemCollectionContainer">
      <div class="search-controls">
        <h3>Item List</h3>
        <div class="search-bar">
          <input
            type="text"
            v-model="globalSearchQuery"
            @input="onGlobalSearch"
            placeholder="Search in all fields (label, type, subject, domain)..."
            class="form-control"
          />
          <button
            v-if="globalSearchQuery"
            @click="clearGlobalSearch"
            class="btn btn-outline-secondary btn-sm clear-btn"
            type="button"
          >
            ✕
          </button>
        </div>
      </div>
      <!-- 🔧 CORRECTION: Utiliser URL relative comme StudentList -->
      <TabulatorRemote
        ref="tabulatorRef"
        tableType="items"
        :apiUrl="`/items`"
        :page-size="10"
        :columns="columns"
        :filters="filters"
        :height="'auto'"
        @select-item="(item) => emit('select-item', item)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref,onMounted, defineExpose, computed } from 'vue';
import PageHeader from '@/components/common/PageHeader.vue';
import TabulatorRemote from '@/components/common/TabulatorRemote.vue';
import { itemColumns } from '@/columns/items';
import { convertQtiToWasplChoice } from '@/utils/qtiToWaspl';
import { getUNESCOLabelSync } from '@/utils/unescoSubjects';
import api from "@/services/axios";

const items = ref([]);
const columns = itemColumns((id) => deleteItem(id));
const selectedItem = ref(null);
const tabulatorRef = ref(null);

const emit = defineEmits(['select-item']);

// 🔧 CORRECTION: Garder l'URL complète pour les autres appels API
const fullApiUrl = import.meta.env.VITE_API_BASE_URL + '/items';

// Statistics data
const statistics = ref({
  totalItems: 0,
  totalSubjects: 0,
  typeDistribution: {}
});

// Limiter l'affichage à 5 types maximum pour les cartes
const limitedTypeDistribution = computed(() => {
  const entries = Object.entries(statistics.value.typeDistribution);
  return entries.slice(0, 5).reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
});

// Fetch statistics
const fetchStatistics = async () => {
  try {
    const response = await api.get('/items/statistics');
    statistics.value = response.data;
  } catch (error) {
    console.error('Error fetching item statistics:', error);
  }
};

// Get card color class based on index
const getTypeCardClass = (index) => {
  const classes = ['stat-card-green', 'stat-card-orange', 'stat-card-teal', 'stat-card-pink', 'stat-card-indigo'];
  return classes[index % classes.length];
};

// Get icon based on question type
const getTypeIcon = (type) => {
  const icons = {
    choice: 'bi bi-check-circle',
    choiceBlock: 'bi bi-ui-checks-grid',
    textGaps: 'bi bi-input-cursor-text',
    shortAnswer: 'bi bi-pencil-square',
    order: 'bi bi-sort-numeric-down',
    match: 'bi bi-diagram-3',
    makingPairs: 'bi bi-link-45deg',
    hotSpot: 'bi bi-cursor',
    message: 'bi bi-chat-text',
    default: 'bi bi-question-circle'
  };
  return icons[type] || 'bi bi-question-circle';
};

// Format type name for display
const formatTypeName = (type) => {
  const names = {
    choice: 'Choice',
    choiceBlock: 'Choice Block',
    textGaps: 'Text Gaps',
    shortAnswer: 'Short Answer',
    order: 'Order',
    match: 'Match',
    makingPairs: 'Making Pairs',
    hotSpot: 'Hot Spot',
    message: 'Message',
    default: 'Default'
  };
  return names[type] || type;
};

function triggerFileSelect() {
  document.getElementById('file-input').click();
}

// Fonction pour charger les items depuis l'API
const loadItems = async () => {
  try {
    const response = await api.get('/items');
    items.value = response.data;
    console.log('Items loaded:', items.value);
  } catch (error) {
    console.error('Failed to load items', error);
  }
};

// Variables pour la recherche globale
const globalSearchQuery = ref('');
let searchTimeout = null;

// Fonction de recherche globale avec debouncing
const onGlobalSearch = () => {
  // Annuler la recherche précédente si elle est en cours
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  // Attendre 300ms avant d'effectuer la recherche
  searchTimeout = setTimeout(() => {
    performSearch();
  }, 300);
};

// Fonction qui effectue réellement la recherche
const performSearch = () => {
  const searchTerm = globalSearchQuery.value.trim();
  
  if (!searchTerm) {
    // Effacer la recherche - retour aux filtres par défaut
    filters.value = [];
  } else {
    // Créer un filtre de recherche globale qui sera envoyé au serveur
    filters.value = [{
      field: 'global_search',
      type: 'like',
      value: searchTerm
    }];
  }
};

// Fonction pour effacer la recherche
const clearGlobalSearch = () => {
  globalSearchQuery.value = '';
  filters.value = []; // Effacer immédiatement
};

// Appel initial
onMounted(() => {
  console.log("ITEM LIST MOUNTED")
  console.log("correction 5 full columns tabletype")
  loadItems();
  fetchStatistics();
});

// ✅ expose loadItems() pour le parent
defineExpose({
  reloadTable: () => {
    tabulatorRef.value?.reloadData();
  }
});

async function handleQtiImport(event) {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = async () => {
    try {
      const xmlString = reader.result;
      const converted = convertQtiToWasplChoice(xmlString);

      const token = localStorage.getItem('editortoken');

      // 🔧 CORRECTION: Utiliser l'URL complète pour les appels POST
      const res = await fetch(fullApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(converted),
      });

      if (!res.ok) throw new Error('Erreur lors de l\'envoi du JSON');
      alert('✅ Item importé avec succès');
      tabulatorRef.value?.reloadData();

    } catch (e) {
      console.error('❌ Import QTI échoué', e);
      alert('Erreur lors de l\'import du fichier QTI');
    }
  };

  reader.readAsText(file);
}

// 🧼 Fonction pour supprimer un item et mettre à jour la vue
async function deleteItem(el_ID) {
  try {
    await api.delete(`/items/${el_ID}`);

    console.log(`✅ Item ${el_ID} supprimé`);

    tabulatorRef.value?.reloadData();
    emit('select-item', null);

  } catch (err) {
    console.error(`❌ Erreur suppression ${el_ID}`, err);
    alert("Erreur lors de la suppression de l'item.");
  }
}

const searchQuery = ref('')
const filters = ref([])

const onSearch = () => {
  filters.value = [
    { field: 'title', type: 'like', value: searchQuery.value }
  ]
}
</script>

<style scoped>
/* Conteneur principal avec padding uniforme */


.ItemCollectionContainer {
  margin: 20px 0;
  padding: 10px;
  border-radius: 5px;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

/* Bouton Import QTI moderne */
.btn-import-qti {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(123, 31, 162, 0.3);
}

.btn-import-qti i {
  font-size: 1.1rem;
}

.btn-import-qti:hover {
  background: linear-gradient(135deg, #6A1B9A 0%, #4A148C 100%);
  box-shadow: 0 4px 12px rgba(123, 31, 162, 0.4);
  transform: translateY(-2px);
}

.btn-import-qti:active {
  transform: translateY(0);
}

/* Styles pour la barre de recherche */
.search-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  gap: 20px;
}

.search-bar {
  position: relative;
  flex: 1;
  max-width: 500px;
}

.search-bar input {
  width: 100%;
  padding-right: 35px;
}

.clear-btn {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  border: none !important;
  background: none !important;
  padding: 2px 6px;
  font-size: 14px;
  color: #6c757d;
}

.clear-btn:hover {
  color: #dc3545;
  background: none !important;
}

@media (max-width: 768px) {
  .search-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .search-bar {
    max-width: none;
  }
}

/* Statistics Indicators */
.statistics-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin: 20px 0;
  padding: 0;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  flex-shrink: 0;
}

.stat-content {
  flex-grow: 1;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Blue theme */
.stat-card-blue {
  border-left-color: #6CB4EE;
}

.stat-card-blue .stat-icon {
  background-color: #E3F2FD;
  color: #1976D2;
}

.stat-card-blue .stat-value {
  color: #1565C0;
}

/* Green theme */
.stat-card-green {
  border-left-color: #77DD77;
}

.stat-card-green .stat-icon {
  background-color: #E8F5E9;
  color: #388E3C;
}

.stat-card-green .stat-value {
  color: #2E7D32;
}

/* Purple theme */
.stat-card-purple {
  border-left-color: #B19CD9;
}

.stat-card-purple .stat-icon {
  background-color: #F3E5F5;
  color: #7B1FA2;
}

.stat-card-purple .stat-value {
  color: #6A1B9A;
}

/* Orange theme */
.stat-card-orange {
  border-left-color: #FFB347;
}

.stat-card-orange .stat-icon {
  background-color: #FFF3E0;
  color: #E65100;
}

.stat-card-orange .stat-value {
  color: #D84315;
}

/* Teal theme */
.stat-card-teal {
  border-left-color: #5FB7C0;
}

.stat-card-teal .stat-icon {
  background-color: #E0F2F1;
  color: #00695C;
}

.stat-card-teal .stat-value {
  color: #004D40;
}

/* Pink theme */
.stat-card-pink {
  border-left-color: #F2A8C7;
}

.stat-card-pink .stat-icon {
  background-color: #FCE4EC;
  color: #C2185B;
}

.stat-card-pink .stat-value {
  color: #880E4F;
}

/* Indigo theme */
.stat-card-indigo {
  border-left-color: #9FA8DA;
}

.stat-card-indigo .stat-icon {
  background-color: #E8EAF6;
  color: #3949AB;
}

.stat-card-indigo .stat-value {
  color: #283593;
}

/* Responsive design */
@media (max-width: 768px) {
  .statistics-container {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 15px;
  }

  .stat-card {
    padding: 15px;
  }

  .stat-icon {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }

  .stat-value {
    font-size: 1.5rem;
  }
}
</style>